import { ShieldAlert } from "lucide-react";
import React, { useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";

interface SuspendedOrganizationDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  suspensionReason?: string | null;
  creditLimitResetAt?: string | null;
  suspendedAt?: string | null;
}

const REASON_CONFIG: Record<string, { title: string; description: string }> = {
  CREDIT_LIMIT_REACHED: {
    title: "Credit Limit Reached",
    description: "Your organization has exceeded its credit limit. All agents are paused.",
  },
  SUBSCRIPTION_ENDED: {
    title: "Subscription Ended",
    description: "Your organization's subscription has ended. All agents are paused. Please contact your admin to renew.",
  },
  EVALUATION_ENDED: {
    title: "Evaluation Period Ended",
    description: "Your organization's evaluation period has ended. All agents are paused.",
  },
};

const DEFAULT_CONFIG = {
  title: "Account Suspended",
  description: "Your organization has been suspended. All agents are paused.",
};

function formatUtcDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function formatLocalDate(isoDate: string): string {
  const date = new Date(isoDate);
  const dateStr = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dateStr} at ${timeStr} (local)`;
}

const ToggleableTimestamp = React.memo<{ isoDate: string }>(function ToggleableTimestamp({ isoDate }) {
  const [showLocal, setShowLocal] = useState(false);
  const label = showLocal ? formatLocalDate(isoDate) : `${formatUtcDate(isoDate)} UTC`;
  return (
    <span
      role="button"
      tabIndex={0}
      className="cursor-pointer border-b border-dotted border-current"
      onClick={() => setShowLocal((v) => !v)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowLocal((v) => !v); }}
    >
      {label}
    </span>
  );
});

export const SuspendedOrganizationDialog: React.FC<SuspendedOrganizationDialogProps> = ({
  isOpen,
  onDismiss,
  suspensionReason,
  creditLimitResetAt,
  suspendedAt,
}) => {
  const config = (suspensionReason && REASON_CONFIG[suspensionReason]) || DEFAULT_CONFIG;
  const manualSuspensionBlocksReset = suspendedAt && creditLimitResetAt && new Date(suspendedAt) <= new Date(creditLimitResetAt);
  const showResetDate = suspensionReason === "CREDIT_LIMIT_REACHED" && creditLimitResetAt && !manualSuspensionBlocksReset;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onDismiss(); }}>
      <DialogContent variant="medium">
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 rounded-full bg-destructive/10 p-3">
            <ShieldAlert className="h-8 w-8 text-destructive" />
          </div>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>
            {config.description}
            {showResetDate && (
              <> Usage will reset on <ToggleableTimestamp isoDate={creditLimitResetAt} />.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center">
          <Button onClick={onDismiss} variant="outline" autoFocus>
            Dismiss
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
